/**
 * Estado visual derivado de una `TrainingSession` (Sprint 8 S8.2 / UX-006).
 *
 * El backend persiste `completion_status` con 4 valores enum:
 * `planned | completed | partial | skipped`. La UI necesita un quinto
 * estado **derivado**, `overdue` (planned + fecha pasada), para hacer
 * evidente al atleta que olvidó reportar o saltar la sesión.
 *
 * Esta utility:
 * - Calcula el estado visual a partir de la sesión + `today` inyectable
 *   (testeable sin mockear `Date`).
 * - Expone una tabla de presentación canónica (clases Tailwind + icono
 *   Unicode + clave i18n) consumida por SessionCard, SessionDetailPage y
 *   cualquier futuro componente que muestre estado de sesión.
 *
 * **No persistir** este estado: es 100% derivado. Si el backend añade
 * algún día una columna `is_overdue`, esta utility la lee, pero el motor
 * NO debería tomar decisiones basadas en "overdue" — la lógica de
 * adaptación trabaja con `completion_status` puro.
 */

import type { CompletionStatus, TrainingSession } from "@/types/api";

/** Estado visual que la UI puede renderizar. Incluye `overdue` derivado. */
export type SessionVisualState =
  | "planned"
  | "completed"
  | "partial"
  | "skipped"
  | "overdue";

/** ISO YYYY-MM-DD del día actual. Extraído para inyectar en tests. */
function _todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Resuelve el estado visual de una sesión.
 *
 * Reglas (en orden de precedencia):
 * 1. `completed | partial | skipped` → se mantienen como el persistido.
 *    Una vez que el atleta reportó algo, ya no hay ambigüedad temporal.
 * 2. `planned` + `session_date < today` → `overdue` (alerta UX).
 * 3. `planned` + fecha hoy o futura → `planned` (estado normal).
 *
 * @param session - sesión del plan tal como llega del backend.
 * @param today  - ISO date opcional para tests deterministas. Default:
 *                 fecha actual del navegador (UTC truncada al día).
 */
export function getSessionVisualState(
  session: Pick<TrainingSession, "completion_status" | "session_date">,
  today: string = _todayIso(),
): SessionVisualState {
  if (session.completion_status !== "planned") {
    // completed / partial / skipped → estado final, sin reinterpretar.
    return session.completion_status as SessionVisualState;
  }
  // planned: distinguir overdue (pasada sin reporte) vs planned futuro.
  if (session.session_date < today) {
    return "overdue";
  }
  return "planned";
}

/**
 * Clases Tailwind del badge según estado visual.
 *
 * Decisión cromática (matching design tokens existentes):
 * - planned: neutral grey (sin alarma — sesión futura).
 * - completed: success token verde.
 * - partial: warning token amarillo low-opacity.
 * - skipped: danger token rojo low-opacity.
 * - overdue: warning token alto-opacity (más punch que partial) — el
 *   atleta debe accionar (reportar o saltar). NO usamos danger porque
 *   "overdue" no es error, es "atención pendiente".
 */
export const VISUAL_STATE_BADGE_CLASSES: Record<SessionVisualState, string> = {
  planned: "bg-carbon text-bone-muted border-border-subtle",
  completed: "bg-success/15 text-success border-success/30",
  partial: "bg-warning/15 text-warning border-warning/30",
  skipped: "bg-danger/10 text-danger border-danger/30",
  overdue: "bg-warning/25 text-warning border-warning/60",
};

/**
 * Iconos Unicode por estado. Sutiles, mejoran scanability a vista rápida.
 * Cadena vacía = sin icono (planned no necesita decoración, es neutral).
 *
 * Caracteres elegidos en BMP estándar para render consistente sin fonts
 * extras:
 * - ✓ U+2713  completed (check)
 * - ◐ U+25D0  partial (medio círculo)
 * - ⏭ U+23ED  skipped (skip-track)
 * - ⚠ U+26A0  overdue (warning)
 */
export const VISUAL_STATE_ICONS: Record<SessionVisualState, string> = {
  planned: "",
  completed: "✓",
  partial: "◐",
  skipped: "⏭",
  overdue: "⚠",
};

/**
 * Clave i18n de cada estado en `t.plan`. Aquí mapeamos completion_status
 * + overdue derivado. Los strings literales viven en `i18n/es.ts`.
 */
export const VISUAL_STATE_I18N_KEY: Record<SessionVisualState, string> = {
  planned: "statusPlanned",
  completed: "statusCompleted",
  partial: "statusPartial",
  skipped: "statusSkipped",
  overdue: "statusOverdue",
};

/** Tipo helper: garantiza que un VisualState mapea 1:1 con CompletionStatus
 *  excepto por `overdue` (derivado). Utilizado por callers que pivotan
 *  decisiones según un valor persistido en BD. */
export function isPersistedCompletionStatus(
  state: SessionVisualState,
): state is CompletionStatus {
  return state !== "overdue";
}
